//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  InfiniteData,
  QueryClient,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import {
  ContentLibraryV2RequestBody,
  IContentLibraryItem,
  IContentLibraryItemsResponse,
  ManualModeratorMediaStatus,
} from '@apis/content-library/contentLibraryApiModel';
import { isManualModerationListRequest } from '@media-library/moderation/constants/ModerationConstants';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

export const partitionModeratorUnshareSelection = (
  cuids: readonly Cuid[],
  items: readonly IContentLibraryItem[],
  clientId: ClientId,
) => {
  const statuses = new Map<Cuid, ManualModeratorMediaStatus | undefined>();
  items.forEach((item) => {
    const cuid = Cuid.build(item, clientId);
    const status = item.manualModeratorMediaMetadata?.status;
    // Conflicting metadata must never make an item eligible.
    statuses.set(
      cuid,
      statuses.has(cuid) && statuses.get(cuid) !== status
        ? undefined
        : status,
    );
  });

  const eligibleCuids: Cuid[] = [];
  let reviewedCount = 0;
  let unknownCount = 0;
  [...new Set(cuids)].forEach((cuid) => {
    switch (statuses.get(cuid)) {
      case ManualModeratorMediaStatus.UnderReview:
      case ManualModeratorMediaStatus.NeedsProduct:
        eligibleCuids.push(cuid);
        break;
      case ManualModeratorMediaStatus.Approved:
      case ManualModeratorMediaStatus.Published:
      case ManualModeratorMediaStatus.Rejected:
        reviewedCount++;
        break;
      default:
        unknownCount++;
    }
  });
  return { eligibleCuids, reviewedCount, unknownCount };
};

export const partitionModeratorForceReviewSelection = (
  cuids: readonly Cuid[],
  items: readonly IContentLibraryItem[],
  clientId: ClientId,
) => {
  const removal = partitionModeratorUnshareSelection(cuids, items, clientId);
  const needsProductCuids = new Set(
    items
      .filter(
        (item) =>
          item.manualModeratorMediaMetadata?.status ===
          ManualModeratorMediaStatus.NeedsProduct,
      )
      .map((item) => Cuid.build(item, clientId)),
  );
  const eligibleCuids = removal.eligibleCuids.filter((cuid) =>
    needsProductCuids.has(cuid),
  );
  return {
    ...removal,
    eligibleCuids,
    underReviewCount: removal.eligibleCuids.length - eligibleCuids.length,
  };
};

export const getActiveModeratorListData = (queryClient: QueryClient) => {
  const queries = queryClient.getQueryCache().findAll({
    type: 'active',
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      QueryKeyConstants.MediaFiltersMediaItemsInfinite,
    ],
    predicate: (query) =>
      isManualModerationListRequest(
        query.queryKey[3] as ContentLibraryV2RequestBody | undefined,
      ),
  });
  return queries.length === 1
    ? (queries[0]?.state.data as
        InfiniteData<IContentLibraryItemsResponse> | undefined)
    : undefined;
};

export const useModeratorUnshareSelection = (
  cuids: readonly Cuid[],
  clientId: ClientId,
  enabled: boolean,
) => {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient],
  );
  const getSnapshot = useCallback(
    () => (enabled ? getActiveModeratorListData(queryClient) : undefined),
    [queryClient, enabled],
  );
  const data = useSyncExternalStore(subscribe, getSnapshot, () => undefined);
  return partitionModeratorUnshareSelection(
    cuids,
    data?.pages.flatMap((page) => page.contentItems) ?? [],
    clientId,
  );
};
